import React, { useContext, useEffect, useState } from "react";
import basketDeleteRequest from "../../requests/basketDeleteRequest";
import basketContentSearchRequest from "../../requests/basketContentSearchRequest";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { LoginContext } from "../../context/LoginContext";
import { SessionContext } from "../../context/SessionContext";
import { requestError } from "../../utility/requestError";
import { queryCache, useMutation, useQuery } from "react-query";
import BasketDocumentTable from "./BasketDocumentTable";
import BasketEntrySummary from "./BasketEntrySummary";
import { SignificantTerms } from "./SignificantTerms";
import PropTypes from "prop-types";
// Import useStyles last!
import useStyles from "../../useStyles";

/**
 * Renders the contents of a single basket shared from another user.
 *
 * @param basketId The id of the current basket.
 * @param basketTitle The title of the current basket.
 * @param setUpdatedBaskets Callback function to be called when the basket changed.
 * @param updatedBaskets True, if any basket of the current user was changed.
 * @param sharedFrom The owner of the basket.
 * @returns {JSX.Element}
 * @constructor
 */
function SharedBasketEntry({
  basketId,
  basketTitle,
  setUpdatedBaskets,
  updatedBaskets,
  sharedFrom
}) {
  const classes = useStyles();

  const loginContext = useContext(LoginContext);
  const loginState = loginContext.loginState;
  const loginStateDispatcher = loginContext.loginStateDispatcher;
  const sessionContext = useContext(SessionContext);
  const sessionExpiredDispatcher = sessionContext.sessionExpiredDispatcher;

  const [content, setContent] = useState([]);

  // Delete the basket and invalidate the cache for the baskets query.
  const [deleteBaskeMutation] = useMutation(
    ({ basketId }) => {
      basketDeleteRequest(basketId);
    },
    {
      onSuccess: () => {
        queryCache.invalidateQueries("sharedBaskets");
      }
    }
  );

  const handleDelete = () => {
    if (loginState) {
      deleteBaskeMutation({ basketId }).catch(
        requestError(sessionExpiredDispatcher, loginStateDispatcher)
      );
    }
    setUpdatedBaskets(true);
  };

  // Fetch the content of the basket.
  const basketContentQuery = useQuery(
    basketId,
    basketContentSearchRequest(
      basketId,
      sessionExpiredDispatcher,
      loginStateDispatcher
    ),
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: false,
      refetchOnMount: false
    }
  );

  // Get the content of the basket.
  useEffect(() => {
    if (basketContentQuery.data !== undefined) {
      setContent(basketContentQuery.data);
    }
  }, [basketContentQuery.data]);

  return (
    <Accordion key={basketId}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.expansionPanelHeading}
      >
        <BasketEntrySummary
          basketId={basketId}
          basketTitle={basketTitle}
          sharedFrom={sharedFrom}
          onDelete={handleDelete}
        />
      </AccordionSummary>
      {content.hits !== undefined && (
        <AccordionDetails className={classes.expansionPanelDetailsNoPadding}>
          <Grid container>
            <Grid item xs={12} className={classes.resultCard}>
              <BasketDocumentTable shared content={content} />
            </Grid>
            <Grid item xs={12} className={classes.card}>
              <SignificantTerms content={content} basketId={basketId} />
            </Grid>
          </Grid>
        </AccordionDetails>
      )}
    </Accordion>
  );
}

SharedBasketEntry.propTypes = {
  basketId: PropTypes.string.isRequired,
  basketTitle: PropTypes.string.isRequired,
  setUpdatedBaskets: PropTypes.func.isRequired,
  updatedBaskets: PropTypes.bool.isRequired,
  sharedFrom: PropTypes.string.isRequired
};

export default SharedBasketEntry;
